import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { X } from 'lucide-react';
import AdminRichTextEditor from '../components/AdminRichTextEditor.jsx';
import { prepareRichHtmlForApi } from '../utils/richHtml.js';
import { trekAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { canManageTreks as userCanManageTreks } from '../utils/trekPermissions.js';
import { useToast } from '../context/ToastContext';
import AdminBackLink from '../components/AdminBackLink.jsx';
import './ModuleAdmin.css';

const emptySchedule = {
  name: '',
  brief: '',
  description: '',
  countAsDay: 1,
  order: 0,
  imageUrl: ''
};

const TrekScheduleForm = () => {
  const { trekId, scheduleId } = useParams();
  const isEdit = scheduleId && scheduleId !== 'new';
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const { permissions } = useAuth();
  const canManageTreks = userCanManageTreks(permissions);

  const { data: trekRes } = useQuery(
    ['trek', trekId],
    () => trekAPI.get(trekId),
    { enabled: !!trekId }
  );
  const trek = trekRes?.data?.data;

  const { data: scheduleRes } = useQuery(
    ['trek-schedule', trekId],
    () => trekAPI.listScheduleDays(trekId),
    { enabled: !!trekId }
  );
  const scheduleDays = scheduleRes?.data?.data || [];
  /** useParams ids are strings; API returns numeric ids — strict === never matched. */
  const existingSchedule = isEdit
    ? scheduleDays.find((d) => String(d.id) === String(scheduleId))
    : null;

  const [form, setForm] = useState(emptySchedule);
  /** Bumped after hydrate so ReactQuill shows loaded HTML. */
  const [editorMountKey, setEditorMountKey] = useState(0);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const scheduleImageInputRef = useRef(null);
  const didExplicitSubmitRef = useRef(false);
  const isMountedRef = useRef(true);

  useEffect(() => () => {
    isMountedRef.current = false;
  }, []);

  useEffect(() => {
    if (existingSchedule) {
      setForm({
        name: existingSchedule.name || '',
        brief: existingSchedule.brief || '',
        description: existingSchedule.description || '',
        countAsDay: existingSchedule.countAsDay != null ? existingSchedule.countAsDay : 1,
        order: existingSchedule.order != null ? existingSchedule.order : 0,
        imageUrl: existingSchedule.imageUrl || ''
      });
      setEditorMountKey((k) => k + 1);
    } else if (!isEdit) {
      setForm(emptySchedule);
      setEditorMountKey((k) => k + 1);
    }
  }, [existingSchedule, isEdit]);

  const mutation = useMutation(
    (payload) =>
      isEdit
        ? trekAPI.updateScheduleDay(trekId, scheduleId, payload)
        : trekAPI.createScheduleDay(trekId, payload),
    {
      onSuccess: (response) => {
        if (!isMountedRef.current) return;
        // Guard against any unexpected async success firing after remount.
        if (!didExplicitSubmitRef.current) return;
        queryClient.invalidateQueries(['trek-schedule', trekId]);
        // Stay on the editor after save; prevents repeated unexpected auto-close loops.
        if (!isEdit) {
          const createdId = response?.data?.data?.id;
          if (createdId) navigate(`/treks/${trekId}/schedule/${createdId}`, { replace: true });
        }
        showToast({
          message: isEdit ? 'Schedule updated successfully.' : 'Schedule created successfully.',
          severity: 'success'
        });
        didExplicitSubmitRef.current = false;
      },
      onError: (error) => {
        didExplicitSubmitRef.current = false;
        const status = error?.response?.status;
        let message = error?.response?.data?.message || error?.message || 'Failed to save schedule.';
        if (status === 403) {
          message =
            'You do not have permission to save schedule days. Ask a super admin to add Treks – Manage (or Treks) to your role, then log out and log in again.';
        } else if (!error?.response) {
          message =
            'Network error while saving (often large pasted content blocked by the server). Try pasting in smaller sections or ask admin to redeploy the latest admin build.';
        }
        showToast({ message, severity: 'error' });
      }
    }
  );

  const buildPayload = () => {
    const briefPrep = prepareRichHtmlForApi(form.brief);
    const descPrep = prepareRichHtmlForApi(form.description);
    return {
      name: form.name.trim(),
      brief: briefPrep.html,
      description: descPrep.html,
      countAsDay: form.countAsDay ? Number(form.countAsDay) : 0,
      order: form.order ? Number(form.order) : 0,
      imageUrl: form.imageUrl || null,
      _truncated: briefPrep.truncated || descPrep.truncated
    };
  };

  const handleSubmit = () => {
    if (!canManageTreks) {
      showToast({
        message: 'You have view-only Treks access. Ask a super admin for Treks – Manage permission to save.',
        severity: 'warning'
      });
      return;
    }
    if (!form.name.trim()) {
      showToast({
        message: 'Schedule Name is required.',
        severity: 'warning'
      });
      return;
    }
    const built = buildPayload();
    const { _truncated, ...payload } = built;
    if (_truncated) {
      showToast({
        message:
          'Pasted content was very large; part of it was trimmed so the save can complete. Add more in smaller sections if needed.',
        severity: 'warning'
      });
    }
    didExplicitSubmitRef.current = true;
    mutation.mutate(payload);
  };

  const handleUploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setIsUploadingImage(true);
      const response = await trekAPI.uploadImage(file);
      const url = response?.data?.data?.url;
      if (!url) throw new Error('Upload failed');
      setForm((prev) => ({ ...prev, imageUrl: url }));
    } catch (error) {
      showToast({
        message: error?.response?.data?.message || 'Failed to upload image.',
        severity: 'error'
      });
    } finally {
      setIsUploadingImage(false);
      e.target.value = '';
    }
  };

  const clearScheduleImage = () => {
    setForm((prev) => ({ ...prev, imageUrl: '' }));
    if (scheduleImageInputRef.current) scheduleImageInputRef.current.value = '';
  };

  return (
    <div className="module-page">
      <AdminBackLink
        label="Back to treks"
        onNavigate={() => {
          didExplicitSubmitRef.current = true;
          navigate('/treks', { state: { openTrekId: trekId } });
        }}
      />
      <div className="module-header">
        <div>
          <h1 className="module-title">{isEdit ? 'Edit Schedule Day' : 'Add Schedule Day'}</h1>
          <p className="module-subtitle">
            {trek ? trek.name : 'Configure schedule details for trek'}
          </p>
        </div>
      </div>

      <div className="module-card">
        {!canManageTreks ? (
          <p className="legacy-disabled-note" style={{ marginBottom: '0.75rem' }}>
            View-only: you can read this schedule but cannot save. Ask a super admin to grant <strong>Treks – Manage</strong> (or log out and back in after they update your role).
          </p>
        ) : null}
        <form onSubmit={(e) => e.preventDefault()}>
          <div className="module-form-grid">
            <div style={{ gridColumn: '1 / -1' }}>
              <label>Name</label>
              <input
                value={form.name}
                onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                required
              />
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label>Brief</label>
              <AdminRichTextEditor
                key={`sched-brief-${scheduleId || 'new'}-${editorMountKey}`}
                compact
                hint="Short summary shown on the itinerary card (bold, lists, links supported)."
                value={form.brief}
                onChange={(value) => setForm((p) => ({ ...p, brief: value }))}
                placeholder="e.g. The beautiful journey comes to an end!"
              />
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label>Description</label>
              <AdminRichTextEditor
                key={`sched-desc-${scheduleId || 'new'}-${editorMountKey}`}
                hint='Use the toolbar for bold, headings, links. This is the "Know More" content on the event page.'
                value={form.description}
                onChange={(value) => setForm((p) => ({ ...p, description: value }))}
                placeholder="Day details, reporting info, travel options..."
              />
            </div>
            <div>
              <label>Order</label>
              <input
                type="number"
                value={form.order}
                onChange={(e) => setForm((p) => ({ ...p, order: e.target.value }))}
              />
            </div>
            <div>
              <label
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.35rem',
                  fontWeight: 500,
                  whiteSpace: 'nowrap',
                  cursor: 'pointer'
                }}
              >
                <input
                  type="checkbox"
                  checked={!!form.countAsDay}
                  onChange={(e) => setForm((p) => ({ ...p, countAsDay: e.target.checked ? 1 : 0 }))}
                />
                <span>Count as Day</span>
              </label>
            </div>
            <div>
              <label>Image</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                {form.imageUrl ? (
                  <div className="schedule-day-image-preview">
                    <img src={form.imageUrl} alt="Schedule day" />
                    <button
                      type="button"
                      className="schedule-day-image-remove"
                      onClick={clearScheduleImage}
                      title="Remove image"
                      aria-label="Remove image"
                    >
                      <X size={14} strokeWidth={2.5} />
                    </button>
                  </div>
                ) : null}
                <input
                  ref={scheduleImageInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleUploadImage}
                />
                {isUploadingImage ? (
                  <span style={{ fontSize: '0.8rem', color: '#64748b' }}>Uploading...</span>
                ) : null}
              </div>
            </div>
          </div>

          <div className="legacy-main-actions">
            <button
              className="module-btn"
              type="button"
              disabled={mutation.isLoading || !canManageTreks}
              onClick={handleSubmit}
            >
              {isEdit ? 'Update Schedule' : 'Save Schedule'}
            </button>
            <button
              className="module-btn secondary"
              type="button"
              onClick={() => {
                didExplicitSubmitRef.current = true;
                navigate('/treks', { state: { openTrekId: trekId } });
              }}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TrekScheduleForm;

